import type { CustomerSummary, Row } from '@contract';
import { formatMoney } from '@sao/shared/money';
import { createColumnHelper } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { DataTable } from '@/components/data-table';
import { DetailsButton } from '@/components/details-button';
import { StatusPill } from '@/components/primitives/status-pill';
import { OpenRecord } from '@/components/queue/queue-parts';
import { useOpenRecord } from '@/components/sheet/use-sheet';
import { LinkButton } from '@/components/ui/link-button';
import type { LoadMore } from '@/components/ui/table-pager';
import { humanWords, readablePairs } from '@/lib/audit-text';
import { formatDate, formatDateTime } from '@/lib/format';
import { homeCurrency } from '@/lib/market';
import type { SheetRef, SheetType } from '@/lib/sheet-url';

/** How a column's value is shown. */
export type CellKind =
  | 'text'
  | 'mono'
  | 'words'
  | 'number'
  | 'money'
  | 'datetime'
  | 'date'
  | 'status'
  | 'bool'
  | 'json'
  | 'customer'
  | 'transaction'
  | 'issue'
  | 'change'
  | 'staff'
  | 'details';

export interface ColumnSpec {
  key: string;
  label: string;
  kind?: CellKind;
  /** For a `details` column: the record's fields the dialog shows first. */
  fields?: readonly ColumnSpec[];
}

/**
 * A list the server already masked, laid out by a column spec. Amounts read
 * the row's `currency` (the cell's currency when it has none); customer and transaction
 * columns link to their pages.
 */
export function RecordTable({
  columns,
  rows,
  empty = 'Nothing here.',
  renderActions: actions,
  more,
  sheetFor,
  openButtons = true,
}: Readonly<{
  /** A keyset list with more rows on the server: Next loads them. */
  more?: LoadMore;
  columns: readonly ColumnSpec[];
  /** Any server rows; interfaces such as IssueRow fit too. */
  rows: readonly object[] | undefined;
  empty?: ReactNode;
  /** Buttons at the end of each row, such as proposing a change. */
  renderActions?: (row: Row) => ReactNode;
  /** A sheet a row opens instead of the record its identifier columns name. */
  sheetFor?: (row: Row) => SheetRef | undefined;
  /** Show the Open buttons for linked records in an Actions column (default). */
  openButtons?: boolean;
}>) {
  const specColumns = columns.map(specColumn);
  const opens = columns.filter((spec) => LINK_KINDS.has(spec.kind ?? 'text'));
  const shownOpens = openButtons ? opens : [];
  const hasActions = Boolean(actions) || shownOpens.length > 0;
  const openRecord = useOpenRecord();
  const tableColumns = hasActions
    ? [...specColumns, actionsColumn(shownOpens, actions)]
    : specColumns;
  // A new column set is a new table: one table instance whose columns
  // changed under it (the money screens' tabs) froze the page.
  return (
    <DataTable
      key={`${columns.map((spec) => spec.key).join('|')}${hasActions ? '|actions' : ''}`}
      columns={tableColumns}
      data={rows ? (rows as Row[]).slice() : undefined}
      empty={empty}
      more={more}
      rowLabel={(row) =>
        sheetFor?.(row) ? 'Open details' : rowTarget(opens, row)?.label
      }
      onRowClick={(row, rowEl) => {
        const ref = sheetFor?.(row) ?? rowTarget(opens, row)?.ref;
        if (ref) openRecord(ref, rowEl);
      }}
    />
  );
}

const TARGET_NOUN: Record<string, string> = {
  customer: 'customer',
  transaction: 'transaction',
  issue: 'report',
  change: 'change request',
};

/** What a row opens: the first record its identifier columns name. */
function rowTarget(
  opens: readonly ColumnSpec[],
  row: Row,
): { ref: SheetRef; label: string } | undefined {
  for (const spec of opens) {
    const value = row[spec.key];
    if (value === null || value === undefined || value === '') continue;
    const kind = spec.kind ?? 'text';
    const id = kind === 'customer' ? customerId(value) : text(value);
    const type = TARGET_TYPE[kind];
    if (!type) continue;
    return {
      ref: { type, id },
      label: `Open ${TARGET_NOUN[kind]} ${id.slice(0, 8)}`,
    };
  }
  return undefined;
}

const TARGET_TYPE: Record<string, SheetType | undefined> = {
  customer: 'cust',
  transaction: 'tx',
  issue: 'issue',
  change: 'change',
};

const helper = createColumnHelper<Row>();

/** A column laid out by its spec. */
function specColumn(spec: ColumnSpec) {
  return helper.display({
    id: spec.key,
    header: spec.label,
    meta: {
      sortValue: (row) => sortValueOf(spec.kind ?? 'text', row[spec.key]),
    },
    cell: (info) =>
      spec.kind === 'details' ? (
        <DetailsButton
          title={spec.label}
          fields={spec.fields ?? []}
          record={info.row.original}
          detail={info.row.original[spec.key]}
        />
      ) : (
        renderCell(
          spec.kind ?? 'text',
          info.row.original[spec.key],
          info.row.original,
        )
      ),
  });
}

/** What a column of this kind sorts by; a column of raw JSON does not sort. */
function sortValueOf(kind: CellKind, value: unknown): unknown {
  if (kind === 'json') return null;
  if (kind === 'money' || kind === 'number') {
    return value === null || value === undefined || value === ''
      ? null
      : Number(value);
  }
  if (kind === 'transaction' || kind === 'issue' || kind === 'change') {
    return typeof value === 'string' ? value : null;
  }
  return value;
}

/** Identifier columns open their record from the Actions column, not from the text. */
const LINK_KINDS = new Set<CellKind>([
  'customer',
  'transaction',
  'issue',
  'change',
]);

/** The Actions column: an Open button for each record the row names, then the caller's own. */
function actionsColumn(
  opens: readonly ColumnSpec[],
  actions: ((row: Row) => ReactNode) | undefined,
) {
  return helper.display({
    id: 'actions',
    header: 'Actions',
    cell: (info) => (
      <div className="flex flex-wrap justify-end gap-2">
        <OpenButtons opens={opens} row={info.row.original} />
        {actions?.(info.row.original)}
      </div>
    ),
  });
}

function OpenButtons({
  opens,
  row,
}: Readonly<{ opens: readonly ColumnSpec[]; row: Row }>) {
  // With more than one kind of record in a row, the buttons say which.
  const kinds = new Set(opens.map((spec) => spec.kind));
  const named = (kind: string, label: string) =>
    kinds.size > 1 ? `${label} ${kind}` : label;
  return opens.map((spec) => {
    const value = row[spec.key];
    if (value === null || value === undefined || value === '') return null;
    switch (spec.kind) {
      case 'customer': {
        const id = customerId(value);
        return (
          <span key={spec.key} className="flex gap-2">
            <LinkButton
              to="/customers/$customerId"
              params={{ customerId: id }}
              search={{ tab: 'overview' }}
            >
              {named('customer', 'Open')}
            </LinkButton>
            <LinkButton
              to="/customers/$customerId"
              params={{ customerId: id }}
              search={{ tab: 'actions' }}
            >
              Actions
            </LinkButton>
          </span>
        );
      }
      case 'transaction':
        return (
          <LinkButton
            key={spec.key}
            to="/transactions/$transactionId"
            params={{ transactionId: text(value) }}
          >
            {named('transaction', 'Open')}
          </LinkButton>
        );
      case 'issue':
        return (
          <LinkButton
            key={spec.key}
            to="/issues/$issueId"
            params={{ issueId: text(value) }}
          >
            {named('report', 'Open')}
          </LinkButton>
        );
      case 'change':
        return (
          <LinkButton
            key={spec.key}
            to="/approvals/$requestId"
            params={{ requestId: text(value) }}
          >
            {named('change', 'Open')}
          </LinkButton>
        );
      default:
        return null;
    }
  });
}

function customerId(value: unknown): string {
  return typeof value === 'object' && value
    ? (value as CustomerSummary).id
    : text(value);
}

function renderCell(kind: CellKind, value: unknown, row: Row): ReactNode {
  return <Cell kind={kind} value={value} row={row} />;
}

export function Cell({
  kind,
  value,
  row,
}: Readonly<{ kind: CellKind; value: unknown; row?: Row }>): ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">—</span>;
  }
  switch (kind) {
    case 'money':
      return (
        <span className="whitespace-nowrap tabular-nums">
          {formatMoney(Number(value), currencyOf(row))}
        </span>
      );
    case 'datetime':
      return (
        <span className="whitespace-nowrap">{formatDateTime(text(value))}</span>
      );
    case 'date':
      return (
        <span className="whitespace-nowrap">{formatDate(text(value))}</span>
      );
    case 'words':
      return humanWords(text(value));
    case 'mono':
      return <span className="font-mono text-xs">{text(value)}</span>;
    case 'number':
      return <span className="tabular-nums">{text(value)}</span>;
    case 'status':
      return <StatusBadge status={text(value)} />;
    case 'bool':
      return value ? 'Yes' : 'No';
    case 'json':
      return <JsonCell value={value} />;
    case 'customer':
      return <CustomerName value={value} />;
    case 'transaction':
    case 'issue':
    case 'change':
      return (
        <span className="font-mono text-xs">{text(value).slice(0, 8)}</span>
      );
    case 'staff':
      return typeof value === 'object'
        ? text((value as { name?: unknown }).name ?? '')
        : text(value);
    default:
      return text(value);
  }
}

/** A value as text; an object as its JSON, never "[object Object]". */
export function text(value: unknown): string {
  if (typeof value === 'string') return value;
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value.toString();
  }
  return JSON.stringify(value) ?? '';
}

function currencyOf(row: Row | undefined): string {
  return typeof row?.currency === 'string' ? row.currency : homeCurrency();
}

function JsonCell({ value }: Readonly<{ value: unknown }>) {
  const pairs = readablePairs(value);
  if (pairs.length === 0)
    return <span className="text-muted-foreground">—</span>;
  return (
    <dl className="m-0 flex max-w-sm flex-col gap-0.5 text-xs">
      {pairs.map((pair) => (
        <div key={pair.label} className="flex flex-wrap gap-x-1.5">
          <dt className="text-muted-foreground">{pair.label}:</dt>
          <dd className="m-0">{pair.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function CustomerName({ value }: Readonly<{ value: unknown }>) {
  const known = typeof value === 'object' && value;
  const customer = known
    ? (value as CustomerSummary)
    : { id: text(value), phone: null, name: null };
  const shown =
    customer.name ||
    customer.phone ||
    (known ? customer.id.slice(0, 8) : `Customer · ${customer.id.slice(0, 8)}`);
  return (
    <OpenRecord
      sheetRef={{ type: 'cust', id: customer.id }}
      label={`Open customer ${shown}`}
      className="flex flex-col hover:underline"
    >
      <span>{shown}</span>
      {customer.name && customer.phone ? (
        <span className="text-xs text-muted-foreground">{customer.phone}</span>
      ) : null}
    </OpenRecord>
  );
}

export function StatusBadge({ status }: Readonly<{ status: string }>) {
  return <StatusPill status={status} />;
}

/** `key: value` pairs, for a record that is not a list. */
export function FieldList({
  fields,
  record,
}: Readonly<{
  fields: readonly ColumnSpec[];
  record: object | null | undefined;
}>) {
  const values = (record ?? undefined) as Row | undefined;
  return (
    <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-6 gap-y-2 text-sm">
      {fields.map((field) => (
        <div key={field.key} className="contents">
          <dt className="text-muted-foreground">{field.label}</dt>
          <dd className="min-w-0 break-words">
            <Cell
              kind={field.kind ?? 'text'}
              value={values?.[field.key]}
              row={values}
            />
          </dd>
        </div>
      ))}
    </dl>
  );
}
